import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ACCEPT, formatSize, readFiles, type PendingFile } from './attachments.js';
import { Icon } from './Icon.js';

/**
 * Homologacion automatica: el formulario (datos + certificado de notas) y la
 * tarjeta con el resultado que aparece en la conversacion.
 *
 * El servidor lee el certificado con el modelo, aplica la plantilla de
 * equivalencias de la universidad y registra el expediente
 * (server/src/routes/homologacion.ts). Ver docs/HOMOLOGACION.md.
 */

export type EstadoAsignatura = 'homologada' | 'validacion' | 'no_aprobada' | 'sin_equivalencia';

export interface ResultadoHomologacion {
  institucion: string | null;
  institucionEnPlantilla: boolean;
  malla: string;
  asignaturas: Array<{
    origen: number;
    nombreOrigen: string;
    nota: number | null;
    notaSobre10: number | null;
    anio: number | null;
    materiaUmet: string | null;
    codigoUmet: string | null;
    similitud: number | null;
    estado: EstadoAsignatura;
    motivo: string;
  }>;
  resumen: { total: number; homologadas: number; validacion: number; noAprobadas: number; sinEquivalencia: number; materiasUmetHomologadas: string[] };
}

export interface Expediente {
  id: string;
  /** Estado actual: al volver a la conversacion se ve si ya lo confirmo la Secretaria. */
  estado?: 'preliminar' | 'confirmada' | 'rechazada';
  nombres: string;
  malla: string;
  creadoEn: number;
  resultado: ResultadoHomologacion;
  textoHablado: string;
}

export interface Solicitud {
  /** Lectura hecha al adjuntar (no se vuelve a leer el certificado). */
  lecturaId?: string;
  nombres: string;
  cedula: string;
  correo: string;
  malla: string;
  files: PendingFile[];
}

export interface InfoHomologacion {
  disponible: boolean;
  mallas: string[];
  instituciones: string[];
  /** Consulta de titulos por cedula (SENESCYT) disponible. */
  verificacion?: boolean;
}

export interface ConsultaTitulos {
  encontrado: boolean;
  senescyt: boolean;
  nombre: string | null;
  malla: string | null;
  carreraSugerida: string | null;
  titulos: Array<{
    nivel: string;
    carrera: string | null;
    titulo: string;
    institucion: string;
    fecha: string | null;
    anio: number | null;
    veredicto: 'plantilla' | 'silabos' | 'validacion';
    explicacion: string;
  }>;
  bachiller: { titulo: string; institucion: string; especialidad: string | null; fecha: string | null } | null;
  texto: string;
}

/** Cedula ecuatoriana valida en un texto (admite «171274107-1» o espacios). */
export function cedulaEnTexto(texto: string): string | null {
  for (const m of texto.matchAll(/\d[\d\s-]{8,12}\d/g)) {
    const c = m[0].replace(/\D/g, '');
    if (c.length !== 10) continue;
    const prov = Number(c.slice(0, 2));
    if (!((prov >= 1 && prov <= 24) || prov === 30) || Number(c[2]) >= 6) continue;
    let suma = 0;
    for (let i = 0; i < 9; i++) {
      let d = Number(c[i]) * (i % 2 === 0 ? 2 : 1);
      if (d > 9) d -= 9;
      suma += d;
    }
    if ((10 - (suma % 10)) % 10 === Number(c[9])) return c;
  }
  return null;
}

const ESTADO: Record<EstadoAsignatura, { label: string; tone: string }> = {
  homologada: { label: 'Homologada', tone: 'ok' },
  validacion: { label: 'Validación', tone: 'warn' },
  no_aprobada: { label: 'No aprobada', tone: 'bad' },
  sin_equivalencia: { label: 'Análisis de sílabo', tone: 'muted' },
};

const MENORES = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'e', 'en', 'a', 'al']);
/** «SABERES ANCESTRALES EN SALUD DEL ECUADOR» -> «Saberes Ancestrales en Salud del Ecuador». */
export const titulo = (s: string) =>
  s
    .toLowerCase()
    .split(' ')
    .map((w, i) => (i > 0 && MENORES.has(w) ? w : w.replace(/^(\(?)(.)/, (_, p: string, c: string) => p + c.toUpperCase())))
    .join(' ');
export function HomologacionDialog({
  open,
  info,
  onClose,
  onSubmit,
}: {
  open: boolean;
  info: InfoHomologacion | null;
  onClose: () => void;
  onSubmit: (s: Solicitud) => void;
}) {
  const [nombres, setNombres] = useState('');
  const [cedula, setCedula] = useState('');
  const [correo, setCorreo] = useState('');
  const [malla, setMalla] = useState('');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [acepto, setAcepto] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const firstRef = useRef<HTMLInputElement>(null);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Al abrir: foco en el primer campo y Escape cierra. Solo al abrir; si se
  // repitiera en cada render, el foco saltaria al nombre mientras se escribe.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => firstRef.current?.focus(), 50);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
    window.addEventListener('keydown', esc);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', esc);
    };
  }, [open]);

  useEffect(() => {
    if (open && !malla && info?.mallas.length) setMalla(info.mallas[0]!);
  }, [open, info, malla]);

  if (!open) return null;

  const listo = nombres.trim().length >= 3 && cedula.trim().length >= 5 && !!malla && files.length > 0 && acepto;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!listo) {
      setProblem(
        nombres.trim().length < 3
          ? 'Escribe tus nombres y apellidos.'
          : cedula.trim().length < 5
            ? 'Escribe tu número de cédula o pasaporte.'
            : !files.length
              ? 'Adjunta tu certificado de notas.'
              : 'Marca la autorización para registrar tus datos.',
      );
      return;
    }
    onSubmit({ nombres: nombres.trim(), cedula: cedula.trim(), correo: correo.trim(), malla, files });
    setFiles([]);
    setProblem(null);
  };

  return (
    <div className="hdialog__backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="hdialog" role="dialog" aria-modal="true" aria-labelledby="hdialog-title" onSubmit={submit}>
        <header className="hdialog__head">
          <span className="hdialog__icon" aria-hidden="true">
            <Icon name="cap" size={22} />
          </span>
          <div>
            <h2 id="hdialog-title">Homologación automática</h2>
            <p>Sube tu certificado de notas: el asesor lo lee, lo compara con la plantilla de equivalencias de la UMET y registra tu expediente.</p>
          </div>
          <button type="button" className="hdialog__close" onClick={onClose} aria-label="Cerrar">
            <Icon name="close" />
          </button>
        </header>

        <div className="hdialog__grid">
          <label className="hfield hfield--wide">
            <span>Nombres y apellidos</span>
            <input ref={firstRef} value={nombres} onChange={(e) => setNombres(e.target.value)} autoComplete="name" maxLength={120} />
          </label>
          <label className="hfield">
            <span>Cédula o pasaporte</span>
            <input value={cedula} onChange={(e) => setCedula(e.target.value.replace(/\s/g, ''))} inputMode="text" maxLength={20} />
          </label>
          <label className="hfield">
            <span>
              Correo <em>(opcional)</em>
            </span>
            <input type="email" value={correo} onChange={(e) => setCorreo(e.target.value)} autoComplete="email" maxLength={120} />
          </label>
          <label className="hfield hfield--wide">
            <span>Carrera de la UMET a la que quieres ingresar</span>
            <select value={malla} onChange={(e) => setMalla(e.target.value)}>
              {(info?.mallas ?? []).map((m) => (
                <option key={m} value={m}>
                  {titulo(m)}
                </option>
              ))}
            </select>
          </label>

          <div className="hfield hfield--wide">
            <span>Certificado de notas</span>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              multiple
              hidden
              onChange={async (e) => {
                // Copiar antes de vaciar el campo: vaciarlo vacia tambien su FileList.
                const list = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (!list.length) return;
                const { files: added, problem: p } = await readFiles(list, files.length);
                setFiles((prev) => [...prev, ...added].slice(0, 3));
                setProblem(p);
              }}
            />
            {files.length ? (
              <div className="hfiles">
                {files.map((f) => (
                  <span key={f.id} className="file-chip">
                    <Icon name={f.mediaType === 'application/pdf' ? 'doc' : 'image'} size={16} />
                    <span className="file-chip__name">{f.name}</span>
                    <span className="file-chip__size">{formatSize(f.size)}</span>
                    <button type="button" className="file-chip__remove" aria-label={`Quitar ${f.name}`} onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))}>
                      <Icon name="close" size={14} />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            {files.length < 3 ? (
              <button type="button" className="hupload" onClick={() => fileRef.current?.click()}>
                <Icon name="clip" size={18} />
                <span>{files.length ? 'Añadir otra página' : 'Adjuntar certificado (PDF o foto)'}</span>
              </button>
            ) : null}
          </div>
        </div>

        <label className="hconsent">
          <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} />
          <span>
            Autorizo a la Universidad Metropolitana a registrar mis datos y calificaciones para tramitar mi homologación, y a
            verificar mi cédula en los registros de títulos de la SENESCYT y del Ministerio de Educación. El resultado es
            automático y lo confirma la Secretaría General Técnica.
          </span>
        </label>

        {problem ? (
          <p className="hdialog__problem" role="alert">
            {problem}
          </p>
        ) : null}

        <footer className="hdialog__foot">
          <button type="button" className="text-btn" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="hsubmit" disabled={!listo}>
            <Icon name="check" size={18} />
            <span>Homologar</span>
          </button>
        </footer>
      </form>
    </div>
  );
}

export function ResultadoCard({ exp }: { exp: Expediente }) {
  const r = exp.resultado;
  const s = r.resumen;
  const fecha = new Date(exp.creadoEn).toLocaleDateString('es-EC', { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <article className="hres" aria-label={`Resultado de la homologación ${exp.id}`}>
      <header className="hres__head">
        <div>
          <p className="hres__eyebrow">
            Expediente {exp.id}
            {exp.estado === 'confirmada' ? <span className="hbadge hbadge--ok hres__estado">Confirmada por la Secretaría</span> : null}
            {exp.estado === 'rechazada' ? <span className="hbadge hbadge--bad hres__estado">Revisada por la Secretaría: no procede</span> : null}
          </p>
          <h3>{titulo(r.malla)}</h3>
          <p className="hres__meta">
            {titulo(exp.nombres)} · {r.institucion ? titulo(r.institucion) : 'Institución no identificada'} · {fecha}
          </p>
        </div>
        <div className="hres__score" aria-label={`${s.homologadas} de ${s.total} asignaturas homologadas`}>
          <strong>{s.homologadas}</strong>
          <span>de {s.total}</span>
          <small>homologadas</small>
        </div>
      </header>

      <div className="hres__chips">
        <span className="hbadge hbadge--ok">{s.homologadas} homologadas</span>
        {s.validacion ? <span className="hbadge hbadge--warn">{s.validacion} a validación</span> : null}
        {s.sinEquivalencia ? <span className="hbadge hbadge--muted">{s.sinEquivalencia} a análisis de sílabo</span> : null}
        {s.noAprobadas ? <span className="hbadge hbadge--bad">{s.noAprobadas} no aprobadas</span> : null}
      </div>

      {!r.institucionEnPlantilla ? (
        <p className="hres__note">
          Esta institución aún no tiene plantilla de equivalencias: la Secretaría General Técnica hará el análisis comparativo de los sílabos.
        </p>
      ) : null}

      <div className="hres__table" role="table" aria-label="Asignaturas">
        <div className="hres__row hres__row--head" role="row">
          <span role="columnheader">Tu asignatura</span>
          <span role="columnheader">Nota</span>
          <span role="columnheader">Equivale en la UMET</span>
          <span role="columnheader">Resultado</span>
        </div>
        {r.asignaturas.map((a, i) => (
          <div className="hres__row" role="row" key={`${a.origen}-${a.codigoUmet ?? i}`} title={a.motivo}>
            <span role="cell" className="hres__src">
              {titulo(a.nombreOrigen)}
              {a.anio ? <small>{a.anio}</small> : null}
            </span>
            <span role="cell" className="hres__grade">
              {a.notaSobre10 !== null
                ? a.notaSobre10.toFixed(1).replace('.', ',')
                : a.nota !== null
                  ? String(a.nota).replace('.', ',') // escala no indicada: tal cual
                  : '—'}
            </span>
            <span role="cell" className="hres__dst">
              {a.materiaUmet ? (
                <>
                  {titulo(a.materiaUmet)}
                  <small>{a.similitud !== null ? `${a.similitud.toFixed(0)} % de similitud` : ''}</small>
                </>
              ) : (
                <em>Sin equivalencia en la plantilla</em>
              )}
            </span>
            <span role="cell">
              <span className={`hbadge hbadge--${ESTADO[a.estado].tone}`}>{ESTADO[a.estado].label}</span>
            </span>
          </div>
        ))}
      </div>

      <p className="hres__foot">
        <Icon name="info" size={15} />
        <span>
          Resultado automático según la plantilla de equivalencias de la universidad. La Secretaría General Técnica lo confirma; si no
          estás de acuerdo, puedes pedir la revisión de tu caso.
        </span>
      </p>
    </article>
  );
}

/**
 * Homologar desde el chat: elegir la carrera. Si se detecto en el certificado,
 * se muestra y queda ya elegida (la lista sigue ahi para cambiarla). Sin
 * deteccion: con pocas carreras, un boton por carrera; con muchas, una lista.
 * Pulsar es la autorizacion para registrar el expediente, y el texto lo dice.
 */
export function OfertaCarreras({
  opciones,
  disabled,
  onElegir,
  sugerida,
  carreraOrigen,
}: {
  opciones: string[];
  disabled: boolean;
  onElegir: (malla: string) => void;
  sugerida?: string | null;
  carreraOrigen?: string | null;
}) {
  const [elegida, setElegida] = useState(sugerida && opciones.includes(sugerida) ? sugerida : '');
  const conLista = !!sugerida || opciones.length > 4;
  return (
    <div className="hofrecer">
      {sugerida ? (
        <p className="hofrecer__detectada">
          {carreraOrigen ? (
            <>
              Carrera en tu certificado: <strong>{titulo(carreraOrigen)}</strong>
              <br />
            </>
          ) : null}
          Carrera de la UMET: <strong>{titulo(sugerida)}</strong>
        </p>
      ) : null}
      {!conLista ? (
        <div className="hofrecer__btns">
          {opciones.map((m) => (
            <button key={m} type="button" className="hofrecer__btn" disabled={disabled} onClick={() => onElegir(m)}>
              <Icon name="cap" size={18} />
              <span>Homologar en {titulo(m)}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="hofrecer__btns">
          <select className="hofrecer__select" aria-label="Carrera de la UMET" value={elegida} onChange={(e) => setElegida(e.target.value)}>
            {!elegida ? <option value="">Elige tu carrera de la UMET…</option> : null}
            {opciones.map((m) => (
              <option key={m} value={m}>
                {titulo(m)}
              </option>
            ))}
          </select>
          <button type="button" className="hofrecer__btn" disabled={disabled || !elegida} onClick={() => onElegir(elegida)}>
            <Icon name="cap" size={18} />
            <span>{elegida ? `Homologar en ${titulo(elegida)}` : 'Homologar'}</span>
          </button>
        </div>
      )}
      <p className="hofrecer__note">
        Al pulsar autorizas a la Universidad Metropolitana a registrar tus datos y las notas del certificado para tu expediente, y
        a verificar tu cédula en los registros de títulos de la SENESCYT y del Ministerio de Educación. El resultado es
        automático y lo confirma la Secretaría General Técnica.
      </p>
    </div>
  );
}

/** Boton para consultar los titulos con la cedula que escribio (es su autorizacion). */
export function OfertaCedula({ disabled, onConsultar, otraVez }: { disabled: boolean; onConsultar: () => void; otraVez?: boolean }) {
  return (
    <div className="hofrecer">
      <div className="hofrecer__btns">
        <button type="button" className="hofrecer__btn" disabled={disabled} onClick={onConsultar}>
          <Icon name="cap" size={18} />
          <span>{otraVez ? 'Volver a consultar' : 'Consultar mis títulos'}</span>
        </button>
      </div>
      <p className="hofrecer__note">
        Al pulsar autorizas a la Universidad Metropolitana a consultar tu cédula en los registros públicos de títulos de la SENESCYT y
        del Ministerio de Educación. La cédula no se guarda.
      </p>
    </div>
  );
}

const VEREDICTO: Record<ConsultaTitulos['titulos'][number]['veredicto'], { label: string; tone: string }> = {
  plantilla: { label: 'Con plantilla de equivalencias', tone: 'ok' },
  silabos: { label: 'Análisis de sílabos', tone: 'muted' },
  validacion: { label: 'Validación de conocimientos', tone: 'warn' },
};

/** Titulos registrados del estudiante y, para cada uno, como se homologaria. */
export function TitulosCard({ c }: { c: ConsultaTitulos }) {
  return (
    <article className="hres htit" aria-label="Títulos registrados">
      <header className="hres__head">
        <div>
          <p className="hres__eyebrow">Títulos registrados · SENESCYT</p>
          <h3>{c.nombre ? titulo(c.nombre) : 'Sin registros'}</h3>
          {c.carreraSugerida ? <p className="hres__meta">Carrera UMET según tu título: {titulo(c.carreraSugerida)}</p> : null}
        </div>
      </header>
      {!c.senescyt ? (
        <p className="hres__note">La SENESCYT no respondió en este momento. Vuelve a consultar en un minuto.</p>
      ) : c.titulos.length ? (
        <ul className="htit__list">
          {c.titulos.map((t) => (
            <li key={`${t.titulo}-${t.institucion}`}>
              <div>
                <strong>{titulo(t.titulo)}</strong>
                <small>
                  {t.nivel} · {titulo(t.institucion)}
                  {t.anio ? ` · ${t.anio}` : ''}
                </small>
                <p>{t.explicacion}</p>
              </div>
              <span className={`hbadge hbadge--${VEREDICTO[t.veredicto].tone}`}>{VEREDICTO[t.veredicto].label}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="hres__note">No hay títulos de educación superior registrados con esta cédula.</p>
      )}
      <p className="hres__foot">
        <Icon name="info" size={15} />
        <span>
          {c.bachiller
            ? `Bachiller: ${titulo(c.bachiller.titulo)}${c.bachiller.especialidad ? ` en ${titulo(c.bachiller.especialidad)}` : ''}, ${titulo(c.bachiller.institucion)}${c.bachiller.fecha ? ` (${c.bachiller.fecha.slice(0, 4)})` : ''}.`
            : 'No consta título de bachiller, que es requisito.'}{' '}
          Orientación previa: qué asignaturas se homologan sale de tu récord académico, y lo confirma la Secretaría General Técnica.
        </span>
      </p>
    </article>
  );
}
